import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Text, useResponsive } from '@common-fe/common-fe';
import Lottie, { LottieRefCurrentProps } from 'lottie-react';

import ScanningDocs from '@/assets/icons/scanning-docs.json';
import { Modal } from '@/components';
import useProgress from '@/components/controls/DropScan/hooks/useProgress';
import { InnerProgressBar, ProgressBar } from '@/components/controls/DropScan/ProcessScan.styles';

interface Props {
  title?: string;
  onSetVisible: (value: boolean) => void;
  onCancelScanning: () => void;
}

const FileScanningPopup: React.FC<Props> = ({ title, onSetVisible, onCancelScanning }) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const { progress, setProgress } = useProgress();
  const lottieRef = useRef<LottieRefCurrentProps>(null);

  const handleCancelScanning = useCallback(() => {
    setProgress(0);
    onCancelScanning();
    onSetVisible(false);
  }, [setProgress, onCancelScanning, onSetVisible]);

  return (
    <Modal
      visible
      onSetVisible={handleCancelScanning}
      testId="file-scanning"
    >
      <Box pad="spacing24" align="center" justify="center" {...(isMobile ? {} : { flex: 'grow' })}>
        {title && (
          <Text
            data-testid="file-scanning-modal_title"
            size="2xl"
            weight={700}
            textAlign="center"
            margin={{ bottom: 'spacing24' }}
          >
            {title}
          </Text>
        )}

        <Box width={{ width: '100%', max: '400px' }} align="center">
          <Lottie
            lottieRef={lottieRef}
            loop
            data-testId="file-scanning-modal_scanning-icon"
            animationData={ScanningDocs}
            height={128}
            width={128}
          />
          <Text data-testId="file-scanning-modal_scanning-title" margin={{ top: 'spacing24' }} weight={500}>
            {t('Hang tight while we scan your document(s)…')}
          </Text>
          <ProgressBar
            data-testId="file-scanning-modal_scanning-progress-bar"
            margin={{ vertical: 'spacing12' }}
            width="100% !important"
          >
            <InnerProgressBar progress={progress} />
          </ProgressBar>
          <Text>{`${Math.floor(progress * 100)}% Scanned`}</Text>
        </Box>
      </Box>
    </Modal>
  );
};

export default FileScanningPopup;
